import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Phone, RefreshCcw, CheckCircle2, Trash2, Calendar as CalendarIcon, IndianRupee, Wallet, KeyRound, Download, RotateCcw, Copy, Share2,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { endpoints } from "../lib/api";
import ProgressCard from "../components/ProgressCard";
import AISuggestPanel from "../components/AISuggestPanel";
import AIPlanCard from "../components/AIPlanCard";
import OnboardingTimeline from "../components/OnboardingTimeline";
import PaymentTracker from "../components/PaymentTracker";
import RiskBadge from "../components/RiskBadge";
import WaiverSection from "../components/WaiverSection";
import MemberDetailsSection from "../components/MemberDetailsSection";
import OnboardingModal from "../components/OnboardingModal";
import { waLink, openWhatsApp, templates } from "../lib/wa";
import WaButton from "../components/WaButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState({ duration_days: 30, plan: "Monthly", plan_amount: 2000 });
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 2000, method: "cash", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        endpoints.getMember(id),
        endpoints.getSettings().catch(() => ({ data: null })),
      ]);
      setM(mRes.data);
      setSettings(sRes.data);
      setRenewForm({ duration_days: 30, plan: mRes.data.plan || "Monthly", plan_amount: mRes.data.plan_amount || 2000 });
    } catch {
      toast.error("Member not found");
      navigate("/members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // If the user arrived from a Task "Manage" CTA (?wa=task), auto-open
  // WhatsApp with a prefilled message after the member has loaded.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!m || searchParams.get("wa") !== "task") return;
    const days = m.expiry_date ? Math.ceil((new Date(m.expiry_date) - new Date()) / 86400000) : 9999;
    const msg = days <= 7 ? templates.renewal(m.name, Math.max(days, 1)) : templates.inactivity(m.name);
    openWhatsApp(m.phone, msg);
    // Clean the param so refresh doesn't re-open
    const next = new URLSearchParams(searchParams);
    next.delete("wa");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m]);

  if (loading || !m) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const markVisit = async () => {
    await endpoints.markVisit(id);
    toast.success("Visit recorded");
    load();
  };

  const renew = async (e) => {
    e.preventDefault();
    await endpoints.renewMember(id, {
      duration_days: parseInt(renewForm.duration_days) || 30,
      plan: renewForm.plan,
      plan_amount: parseFloat(renewForm.plan_amount) || 0,
    });
    toast.success("Membership renewed 💪");
    setRenewOpen(false);
    load();
  };

  const addPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    await endpoints.addPayment(id, {
      amount: parseFloat(payForm.amount),
      method: payForm.method,
      note: payForm.note,
    });
    toast.success("Payment recorded");
    setPayOpen(false);
    setPayForm({ amount: renewForm.plan_amount || 2000, method: "cash", note: "" });
    load();
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await endpoints.deletePayment(id, paymentId);
      toast.success("Payment removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const regeneratePin = async () => {
    if (!window.confirm("Regenerate PIN? The old PIN will stop working immediately.")) return;
    try {
      await endpoints.regeneratePin(id);
      toast.success("New PIN generated");
      load();
    } catch {
      toast.error("Could not regenerate PIN");
    }
  };

  const copyPin = () => {
    if (!m?.pin) return;
    navigator.clipboard.writeText(m.pin).then(
      () => toast.success("PIN copied"),
      () => toast.error("Copy failed"),
    );
  };

  const downloadQR = () => {
    const canvas = document.getElementById("member-qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(m.name || "member").replace(/\s+/g, "_")}_checkin_qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const sharePortal = async () => {
    if (!m?.attendance_code) return;
    const link = `${window.location.origin}/me/${m.attendance_code}`;
    // 1) Web Share API (mobile / supported browsers). Pass ONLY the link as
    //    `text` so WhatsApp doesn't show it twice (preview-card + body).
    if (navigator.share) {
      try {
        await navigator.share({ title: "Your Fitnudge portal", text: link });
        return;
      } catch (err) {
        // User cancelled - exit silently. Anything else falls through to
        // clipboard so the link is still recoverable.
        if (err && err.name === "AbortError") return;
      }
    }
    // 2) Clipboard
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success("Portal link copied");
        return;
      }
    } catch (_) { /* fall through */ }
    // 3) Final fallback - prompt the user with the link so they can copy it
    try {
      window.prompt("Copy this portal link:", link);
    } catch (_) {
      toast.error("Could not share. Link: " + link);
    }
  };

  const exp = new Date(m.expiry_date);
  const now = new Date();
  const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  const lv = m.last_visit ? new Date(m.last_visit) : null;
  const lvDays = lv ? Math.floor((now - lv) / (1000 * 60 * 60 * 24)) : null;

  const waMsg = days <= 7 ? templates.renewal(m.name, Math.max(days, 1)) : templates.inactivity(m.name);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl mx-auto">
      <Link to="/members" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-members">
        <ArrowLeft className="w-4 h-4" /> Members
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-8" data-testid="member-detail-header">
        {/* Profile + identity */}
        <div className="flex items-start gap-4 min-w-0 flex-1">
          {/* Avatar / profile image placeholder */}
          <div className="shrink-0">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 border border-border flex items-center justify-center text-foreground font-display font-bold text-2xl"
              data-testid="member-avatar"
              aria-label="Member avatar"
            >
              {(m.name || "M").trim().split(/\s+/).slice(0, 2).map(s => s[0]).join("").toUpperCase()}
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground truncate" data-testid="member-detail-name">
                {m.name}
              </h1>
              <RiskBadge level={m.risk_level} factors={m.risk_factors || []} size="md" />
            </div>
            <div className="flex items-center gap-2 mt-1.5 text-xs sm:text-sm text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{m.phone}</span>
              <span className="text-muted-foreground/40">·</span>
              <span>{m.plan}</span>
              {m.pin && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="inline-flex items-center gap-1" data-testid="member-header-pin">
                    <KeyRound className="w-3.5 h-3.5" />
                    PIN <span className="font-mono font-semibold text-foreground tracking-widest">{m.pin}</span>
                  </span>
                </>
              )}
              {lvDays !== null && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="inline-flex items-center gap-1" data-testid="member-header-lastvisit">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Last visit {lvDays}d ago
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

          {/* Action buttons - compact */}
          <div className="flex items-center gap-1.5 flex-wrap shrink-0">
            <WaButton
              phone={m.phone}
              text={waMsg}
              testId="member-wa-primary"
              variant="primary"
              size="md"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Message
            </WaButton>
          <button
            onClick={markVisit}
            data-testid="mark-visit-btn"
            className="inline-flex items-center gap-1.5 bg-card border border-border hover:bg-muted/50 text-foreground text-xs font-semibold px-3 py-2 rounded-md transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark visit
          </button>
          <Dialog open={payOpen} onOpenChange={(v) => { setPayOpen(v); if (v) setPayForm({ amount: m.plan_amount || 2000, method: "cash", note: "" }); }}>
            <DialogTrigger asChild>
              <button data-testid="mark-paid-btn" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-md transition-colors">
                <Wallet className="w-3.5 h-3.5" /> Mark paid
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Record payment</DialogTitle></DialogHeader>
              <form onSubmit={addPayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input
                      type="number"
                      data-testid="payment-amount-input"
                      value={payForm.amount}
                      onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Method</Label>
                    <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                      <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["cash", "upi", "card", "bank_transfer", "other"].map(opt => (
                          <SelectItem key={opt} value={opt}>{opt.replace("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input
                    data-testid="payment-note-input"
                    value={payForm.note}
                    onChange={e => setPayForm({ ...payForm, note: e.target.value })}
                    placeholder="e.g., Jan 2026 renewal"
                  />
                </div>
                <DialogFooter>
                  <button type="submit" data-testid="payment-submit-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors">
                    Record payment
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
            <DialogTrigger asChild>
              <button data-testid="renew-btn" className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-3 py-2 rounded-md transition-colors">
                <RefreshCcw className="w-3.5 h-3.5" /> Renew
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Renew membership</DialogTitle></DialogHeader>
              <form onSubmit={renew} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Plan</Label><Input value={renewForm.plan} onChange={e => setRenewForm({ ...renewForm, plan: e.target.value })} /></div>
                  <div><Label>Amount (₹)</Label><Input type="number" value={renewForm.plan_amount} onChange={e => setRenewForm({ ...renewForm, plan_amount: e.target.value })} /></div>
                  <div><Label>Days</Label><Input type="number" value={renewForm.duration_days} onChange={e => setRenewForm({ ...renewForm, duration_days: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <button type="submit" className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90">
                    Renew
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <button
            onClick={async () => {
              if (!window.confirm("Delete this member?")) return;
              await endpoints.deleteMember(id);
              toast.success("Deleted");
              navigate("/members");
            }}
            className="p-2 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md transition-colors"
            data-testid="delete-member-btn"
            aria-label="Delete member"
            title="Delete member"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Stat label="Expires" value={`${days >= 0 ? days : 0}d`} sub={exp.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} tone={days <= 7 ? "text-orange-600" : "text-foreground"} />
        <Stat label="Plan" value={m.plan} sub={m.plan_amount ? `₹${m.plan_amount}` : ""} />
        <Stat label="Last visit" value={lvDays !== null ? `${lvDays}d ago` : "-"} sub={lv ? lv.toLocaleDateString() : "Never"} tone={lvDays !== null && lvDays > 10 ? "text-rose-600" : "text-foreground"} />
      </div>

      {/* Ready-to-Onboard CTA banner */}
      {m.onboarding_stage === "ready_to_onboard" && (
        <div
          data-testid="ready-to-onboard-banner"
          className="mb-6 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/30 rounded-xl p-4 sm:p-5 flex items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v6m0 8v6M2 12h6m8 0h6" /></svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground">Onboarding pending</p>
              <p className="text-xs text-muted-foreground/85 mt-0.5">
                Capture personal, emergency, and health details to activate the member and unlock automations.
              </p>
            </div>
          </div>
          <button
            onClick={() => setOnboardingOpen(true)}
            data-testid="open-onboarding-modal"
            className="shrink-0 bg-primary text-primary-foreground hover:opacity-90 text-sm font-semibold px-4 py-2 rounded-md"
          >
            Complete onboarding
          </button>
        </div>
      )}

      {/* Phase A - Onboarding timeline + Payment tracker */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <OnboardingTimeline stage={m.onboarding_stage || "membership_active"} history={m.onboarding_history || []} />
        <PaymentTracker member={m} onChanged={load} />
      </div>

      {/* Phase B - Waiver / liability */}
      <div className="mb-6">
        <WaiverSection
          member={m}
          defaultWaiverUrl={settings?.default_waiver_url}
          onChanged={load}
        />
      </div>

      {/* New onboarding flow - Personal/Emergency/Health summary */}
      <MemberDetailsSection member={m} />

      {/* Progress */}
      <ProgressCard memberId={id} />

      {/* AI Assistant */}
      <div className="mb-6">
        <AISuggestPanel
          kind={days <= 7 ? "renewal" : "retention"}
          targetKind="member"
          targetId={m.id}
          phone={m.phone}
        />
      </div>

      {/* AI Workout & Diet Plan */}
      <AIPlanCard
        memberId={m.id}
        memberName={m.name}
        memberPhone={m.phone}
      />

      {/* Check-in credentials */}
      <div className="border border-border rounded-xl p-5 bg-card mb-6" data-testid="checkin-credentials-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-muted-foreground" /> Check-in credentials
          </h3>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Scan or enter the PIN at the kiosk
          </span>
        </div>
        <div className="flex flex-col md:flex-row items-center md:items-start gap-5">
          <div className="shrink-0 p-3 bg-white rounded-xl border border-border" data-testid="member-qr">
            <QRCodeCanvas
              id="member-qr-canvas"
              value={`gympulse:${m.attendance_code || ""}`}
              size={144}
              level="M"
              includeMargin={false}
            />
          </div>
          <div className="flex-1 w-full">
            <div className="flex items-baseline gap-3 mb-1 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PIN</span>
              <span
                className="font-display text-3xl font-bold tracking-[0.3em] text-foreground tabular-nums"
                data-testid="member-pin"
              >
                {m.pin || "-"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground/80 mb-4 max-w-md">
              Share this 4-digit PIN with {m.name}. They can also scan the QR at any Fitnudge kiosk to check in instantly.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={copyPin}
                data-testid="copy-pin-btn"
                className="inline-flex items-center gap-1.5 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-3 py-2 rounded-lg transition-colors"
              >
                <Copy className="w-3.5 h-3.5" /> Copy PIN
              </button>
              <button
                type="button"
                onClick={downloadQR}
                data-testid="download-qr-btn"
                className="inline-flex items-center gap-1.5 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-3 py-2 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download QR
              </button>
              <button
                type="button"
                onClick={regeneratePin}
                data-testid="regenerate-pin-btn"
                className="inline-flex items-center gap-1.5 text-xs bg-card border border-border hover:bg-muted/50 text-rose-600 hover:text-rose-700 px-3 py-2 rounded-lg transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Regenerate PIN
              </button>
              <button
                type="button"
                onClick={sharePortal}
                data-testid="share-portal-btn"
                className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-2 rounded-lg transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" /> Share portal link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payments */}
      <div className="border border-border rounded-xl p-5 bg-card mb-6" data-testid="payments-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-muted-foreground" /> Payments
          </h3>
          {m.payments?.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="payments-total">
              Total: <span className="font-semibold text-foreground">₹{m.payments.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}</span>
            </div>
          )}
        </div>
        {!m.payments || m.payments.length === 0 ? (
          <div className="text-sm text-muted-foreground/70">No payments recorded yet. Use <span className="font-medium text-foreground/85">Mark as paid</span> above.</div>
        ) : (
          <ol className="divide-y divide-border/60">
            {[...(m.payments || [])].sort((a,b) => new Date(b.paid_on) - new Date(a.paid_on)).map((p) => (
              <li key={p.id} className="py-3 flex items-center gap-3" data-testid={`payment-row-${p.id}`}>
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <Wallet className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    ₹{(p.amount || 0).toLocaleString()}
                    <span className="ml-2 text-[10px] font-semibold uppercase text-muted-foreground/70 tracking-wider">{(p.method || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {new Date(p.paid_on).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    {p.note ? ` · ${p.note}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => deletePayment(p.id)}
                  data-testid={`delete-payment-${p.id}`}
                  className="p-1.5 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                  aria-label="Delete payment"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="border border-border rounded-xl p-5 bg-card">
        <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-muted-foreground" /> Messages sent
        </h3>
        {(m.messages || []).length === 0 ? (
          <div className="text-sm text-muted-foreground/70">No messages sent yet.</div>
        ) : (
          <ol className="space-y-3">
            {[...(m.messages || [])].reverse().map((msg, i) => (
              <li key={i} className="text-sm">
                <div className="text-xs text-muted-foreground/70 mb-0.5">
                  {new Date(msg.created_at).toLocaleString()} · {msg.kind}
                  {msg.channel && (
                    <span className={`ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                      msg.channel === "whatsapp" ? "bg-[#25D366]/10 text-[#1EBE5D]" :
                      msg.channel === "sms" ? "bg-blue-50 text-blue-700" :
                      "bg-amber-50 text-amber-700"
                    }`}>{msg.channel}</span>
                  )}
                </div>
                <div className="text-foreground/85">{msg.text}</div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <OnboardingModal
        member={m}
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onChanged={load}
      />
    </div>
  );
}

function Stat({ label, value, sub, tone = "text-foreground" }) {
  return (
    <div className="border border-border rounded-xl p-4 bg-card">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-1">{label}</p>
      <p className={`font-display text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
